/**
 * FlowPreview - Mostra como cada step do onboarding aparece no app mobile.
 *
 * Recebe `steps` (lista plana) e `selectedOrder` (qual step focar). Renderiza
 * dentro de um phone frame replicando os estilos reais do OnboardingScreen
 * (cores, tipografia, ícones, bordas) — apenas representação visual, sem chamadas
 * de API.
 *
 * Paleta canonica do mobile (vinda do OnboardingScreen.tsx):
 *   primary    = #4CAF50  (botoes, header acoes, selecao)
 *   primaryDk  = #1B5E20  (texto selecionado)
 *   primaryLt  = #E8F5E9  (bg de cards selecionados)
 *   textTitle  = #1a1a1a
 *   textBody   = #333
 *   textMuted  = #555 / #666 / #888 / #64748B
 *   inputBg    = #f9f9f9
 *   inputBorder= #ddd
 *   cardBg     = #fafafa
 *   cardBorder = #e0e0e0 / #e8e8e8
 *   infoCardBg = #f8faf8 / borda #d4edda
 *
 * Fontes na tela mobile sao ~22/16/14/13/12. O phone frame aqui tem ~260px
 * (~70% da largura mobile), entao escalei tudo proporcionalmente.
 */

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  StepEmoji,
  TYPE_EMOJI,
  type ConditionalInputOption,
  type DocumentFieldConfig,
  type OnboardingStep,
  type SimulatorPlanPreview,
} from "./FlowEditor";

interface FlowPreviewProps {
  steps: OnboardingStep[];
  /**
   * `id` do step selecionado no canvas (preferencial — sobrevive a reordenacoes
   * e novos inserts). Usamos `id` em vez de `order` porque o `order` so e
   * recalculado quando o FlowNode passa por `flattenFlowTree`, e um step
   * recem adicionado tem `order = 0` ate ser flattenado.
   */
  selectedStepId: string | null;
  /**
   * Planos pra renderizar simulacao real no step `loan_simulator`. Vem do
   * wizard (installments configuradas em "Parcelas"). Quando ha 1+, o preview
   * usa o primeiro pra mostrar valor da parcela / taxa / numero de parcelas.
   */
  simulatorPlans?: SimulatorPlanPreview[];
  /** Valor minimo configurado no wizard — usado como exemplo na simulacao. */
  simulatorValorMinimo?: string;
}

const TIPO_LABELS_PT: Record<string, { singular: string; plural: string }> = {
  diario: { singular: "diaria", plural: "diarias" },
  semanal: { singular: "semanal", plural: "semanais" },
  quinzenal: { singular: "quinzenal", plural: "quinzenais" },
  mensal: { singular: "mensal", plural: "mensais" },
  personalizado: { singular: "personalizada", plural: "personalizadas" },
};

function formatBRL(n: number): string {
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

// ============ Tokens (espelha mobile escalonado a ~70%) ============
const C = {
  primary: "#4CAF50",
  primaryDark: "#1B5E20",
  primaryLight: "#E8F5E9",
  textTitle: "#1a1a1a",
  textBody: "#333",
  textMuted: "#555",
  textHint: "#888",
  textSubtle: "#64748B",
  inputBg: "#f9f9f9",
  inputBorder: "#ddd",
  cardBg: "#fafafa",
  cardBorder: "#e0e0e0",
  cardBorderStrong: "#e8e8e8",
  infoBg: "#E8F5E9",
  infoBorder: "#A5D6A7",
  divider: "#f0f0f0",
};

/**
 * Mantem o texto exatamente como digitado pelo gestor: preserva espacos
 * internos, quebras de linha e indentacao. Apenas remove `\r` (CRLF do
 * Windows) pra renderizar consistente em CSS `white-space: pre-wrap`.
 * NAO usa `.trim()` — espacos no inicio/fim sao intencionais.
 */
function flattenLabel(text: string | undefined): string {
  if (!text) return "";
  return text.replace(/\r/g, "");
}

function PhoneFrame({
  children,
  stepCount,
  currentIndex,
  onPrev,
  onNext,
  emptyMessage,
  isLast,
}: {
  children: React.ReactNode;
  stepCount: number;
  currentIndex: number;
  onPrev: () => void;
  onNext: () => void;
  emptyMessage?: string;
  isLast: boolean;
}) {
  const { t } = useTranslation();
  const isEmpty = stepCount === 0;
  const progress = stepCount > 0 ? ((currentIndex + 1) / stepCount) * 100 : 0;

  return (
    <div className="flex h-full flex-col items-center gap-3 px-3 py-4">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">
        {t("flow_editor.preview.title")}
      </div>

      {/* Phone shell */}
      <div className="relative w-[260px] flex-1 rounded-[36px] border-[10px] border-gray-900 bg-white shadow-xl overflow-hidden dark:bg-gray-100">
        {/* Notch */}
        <div className="absolute left-1/2 top-0 z-10 h-5 w-24 -translate-x-1/2 rounded-b-2xl bg-gray-900" />

        {/* Status bar fake */}
        <div className="h-6" />

        {/* App header (matches mobile: Voltar + step counter) */}
        <div className="flex items-center justify-between px-4 pt-1.5 pb-2.5">
          <span
            className="text-[11px] font-semibold"
            style={{ color: C.primary }}
          >
            ← Voltar
          </span>
          <span className="text-[10px] font-medium" style={{ color: "#666" }}>
            {isEmpty
              ? t("flow_editor.preview.no_steps")
              : `Etapa ${currentIndex + 1}/${stepCount}`}
          </span>
        </div>

        {/* Progress bar */}
        <div className="mx-4 h-1 overflow-hidden rounded-full" style={{ backgroundColor: "#e0e0e0" }}>
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${progress}%`, backgroundColor: C.primary }}
          />
        </div>

        {/* Step content */}
        <div
          className="px-4 pt-4"
          style={{ height: "calc(100% - 110px)", color: C.textBody }}
        >
          {isEmpty ? (
            <div className="flex h-full items-center justify-center text-center text-xs text-gray-500">
              {emptyMessage}
            </div>
          ) : (
            <div className="h-full overflow-y-auto pb-3 pr-1">{children}</div>
          )}
        </div>

        {/* Bottom CTA */}
        <div
          className="absolute bottom-0 left-0 right-0 px-4 py-3"
          style={{ borderTop: `1px solid ${C.divider}`, backgroundColor: "#fff" }}
        >
          <div
            className="h-9 w-full rounded-xl text-center text-[12px] font-bold leading-9 text-white"
            style={{ backgroundColor: C.primary }}
          >
            {isLast ? "Finalizar" : "Continuar"}
          </div>
        </div>
      </div>

      {/* Navigation arrows (fora do phone — controles do preview) */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onPrev}
          disabled={isEmpty || currentIndex <= 0}
          className="h-7 w-7 rounded-full border border-[var(--border)] bg-[var(--card)] text-xs text-[var(--foreground)] hover:bg-[var(--accent)] disabled:opacity-30 disabled:cursor-not-allowed"
          title={t("flow_editor.preview.prev")}
        >
          &#9664;
        </button>
        <div className="min-w-[64px] text-center text-[10px] text-[var(--muted-foreground)]">
          {isEmpty ? "—" : `${currentIndex + 1} / ${stepCount}`}
        </div>
        <button
          type="button"
          onClick={onNext}
          disabled={isEmpty || currentIndex >= stepCount - 1}
          className="h-7 w-7 rounded-full border border-[var(--border)] bg-[var(--card)] text-xs text-[var(--foreground)] hover:bg-[var(--accent)] disabled:opacity-30 disabled:cursor-not-allowed"
          title={t("flow_editor.preview.next")}
        >
          &#9654;
        </button>
      </div>
    </div>
  );
}

// ============ Primitives (mimic mobile styles) ============

/**
 * Cabecalho do step no preview, replicando o padrao visual do mobile:
 *   - Pastilha circular GRANDE com o emoji do tipo (mesmo do canvas)
 *   - Titulo centralizado e bem destacado
 *   - Tudo centralizado, com bom espaco vertical
 *
 * Mobile real: icone ~80px num circulo, titulo 28-32px centralizado.
 * Aqui escalado pro phone frame (~260px largura): icone 48px, titulo 16px.
 */
function StepHeader({
  emoji,
  title,
}: {
  emoji: string;
  title: string;
}) {
  return (
    <div className="mb-3 flex flex-col items-center text-center">
      <div
        className="mb-2 flex h-12 w-12 items-center justify-center rounded-full text-[26px] leading-none"
        style={{
          backgroundColor: C.primaryLight,
          border: `2px solid ${C.primary}33`,
        }}
      >
        {emoji ? <StepEmoji emoji={emoji} size={26} /> : <span>🧩</span>}
      </div>
      <h2
        className="whitespace-pre-wrap break-words text-[15px] font-bold leading-tight"
        style={{ color: C.textTitle, letterSpacing: "-0.2px" }}
      >
        {title || "—"}
      </h2>
    </div>
  );
}

/** Mobile: stepDescription 14px #64748B, marginBottom 20. */
function StepDescription({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="-mt-2 mb-3 whitespace-pre-wrap break-words text-center text-[11px] font-normal leading-snug"
      style={{ color: C.textSubtle }}
    >
      {children}
    </p>
  );
}

/** Mobile: label 14px fw 600 #333 (acima dos inputs). */
function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="mb-1.5 text-[11px] font-semibold"
      style={{ color: C.textBody }}
    >
      {children}
    </div>
  );
}

/** Mobile: input bg #f9f9f9, borda #ddd, rounded 12, padding 14h/16v, fontSize 16. */
function FakeInput({ placeholder, value }: { placeholder?: string; value?: string }) {
  const display = value || placeholder || "";
  return (
    <div
      className="mt-2 rounded-xl px-3 py-2.5 text-[11px]"
      style={{
        backgroundColor: C.inputBg,
        border: `1px solid ${C.inputBorder}`,
        color: value ? C.textTitle : "#999",
      }}
    >
      {display}
    </div>
  );
}

/** Mobile: optionCard com radio 24px (#ccc) → escalei pra 16px. */
function OptionCard({
  children,
  variant = "radio",
}: {
  children: React.ReactNode;
  variant?: "radio" | "checkbox";
}) {
  return (
    <div
      className="flex items-center gap-2.5 rounded-[14px] px-3 py-2.5"
      style={{
        backgroundColor: C.cardBg,
        border: `1.5px solid ${C.cardBorderStrong}`,
      }}
    >
      {variant === "radio" ? (
        <div
          className="h-4 w-4 shrink-0 rounded-full"
          style={{ border: "2px solid #ccc" }}
        />
      ) : (
        <div
          className="h-4 w-4 shrink-0 rounded-md bg-white"
          style={{ border: "2px solid #ccc" }}
        />
      )}
      <span
        className="flex-1 whitespace-pre-wrap break-words text-[11px] font-medium"
        style={{ color: "#444", lineHeight: "16px" }}
      >
        {children}
      </span>
    </div>
  );
}

/**
 * Wrapper branco com sombra, padding interno generoso. Espelha o
 * `referenceCard` do mobile (bg #fff, border 1.5 #e8e8e8, rounded 16,
 * padding 20). Usado nos presets compostos: referencia, email_ou_telefone,
 * chave_pix.
 */
function ReferenceCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="mt-2 rounded-2xl p-3"
      style={{
        backgroundColor: "#fff",
        border: `1.5px solid ${C.cardBorderStrong}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      }}
    >
      {children}
    </div>
  );
}

/** Label uppercase espaco-letrado igual o `referenceLabel` do mobile. */
function ReferenceLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="mb-1 text-[9px] font-bold uppercase"
      style={{ color: C.textHint, letterSpacing: "0.5px" }}
    >
      {children}
    </div>
  );
}

/** Divisor horizontal sutil dentro do referenceCard. */
function ReferenceDivider() {
  return <div className="my-2 h-px" style={{ backgroundColor: C.divider }} />;
}

/**
 * Toggle wider — espelha o `toggleButton` do mobile (flex 1, paddingV 14,
 * borderRadius 12, borderWidth 1.5 #e0e0e0, bg #fafafa). Quando ativo:
 * border #4CAF50, bg #E8F5E9, texto #1B5E20.
 */
function ToggleWide({
  children,
  active = false,
}: {
  children: React.ReactNode;
  active?: boolean;
}) {
  return (
    <div
      className="flex-1 rounded-xl py-2.5 text-center text-[12px]"
      style={{
        backgroundColor: active ? C.primaryLight : C.cardBg,
        border: `1.5px solid ${active ? C.primary : C.cardBorder}`,
        color: active ? C.primaryDark : C.textHint,
        fontWeight: active ? 700 : 600,
      }}
    >
      {children}
    </div>
  );
}

/** Upload/selfie/video box — borda tracejada, bg #fafafa, icon + texto. */
function DashedActionBox({
  icon,
  title,
  hint,
  tall = false,
}: {
  icon: string;
  title: string;
  hint?: string;
  tall?: boolean;
}) {
  return (
    <div
      className={`mt-2 flex flex-col items-center justify-center rounded-[14px] ${tall ? "py-7" : "py-5"}`}
      style={{
        backgroundColor: C.cardBg,
        border: `2px dashed ${C.cardBorder}`,
      }}
    >
      <div className={tall ? "text-[28px]" : "text-[22px]"}>{icon}</div>
      <div
        className={`mt-1.5 ${tall ? "text-[12px]" : "text-[11px]"} font-semibold`}
        style={{ color: C.textMuted }}
      >
        {title}
      </div>
      {hint && (
        <div className="mt-0.5 text-[9px]" style={{ color: "#999" }}>
          {hint}
        </div>
      )}
    </div>
  );
}

/** Card de aceite (terms/info) — checkbox + texto. */
function AcceptCard({ text }: { text: string }) {
  return (
    <div
      className="mt-3 flex items-center gap-3 rounded-[14px] px-3 py-2.5"
      style={{
        backgroundColor: C.cardBg,
        border: `1.5px solid ${C.cardBorder}`,
      }}
    >
      <div
        className="h-4 w-4 shrink-0 rounded-md bg-white"
        style={{ border: "2px solid #ccc" }}
      />
      <span
        className="flex-1 whitespace-pre-wrap break-words text-[11px] font-medium leading-snug"
        style={{ color: C.textMuted }}
      >
        {text}
      </span>
    </div>
  );
}

// ============ Per-step renderers ============

function StepPreview({
  step,
  simulatorPlans,
  simulatorValorMinimo,
}: {
  step: OnboardingStep;
  simulatorPlans?: SimulatorPlanPreview[];
  simulatorValorMinimo?: string;
}) {
  const { t } = useTranslation();
  const label = flattenLabel(step.config.label);
  const placeholder = step.config.placeholder;
  const message = step.config.message;
  const description = (step.config as { description?: string }).description;
  const emoji = TYPE_EMOJI[step.type] ?? "🧩";

  /** Reference (text_input + message=telefone) — espelha o mobile (referenceCard com 2 fields) */
  if (step.type === "text_input" && message === "telefone") {
    return (
      <div>
        <StepHeader emoji={emoji} title={label} />
        {description && <StepDescription>{description}</StepDescription>}
        <ReferenceCard>
          <ReferenceLabel>Nome completo</ReferenceLabel>
          <FakeInput placeholder={placeholder || t("flow_editor.preview.ph.name")} />
          <ReferenceDivider />
          <ReferenceLabel>Telefone / WhatsApp</ReferenceLabel>
          <FakeInput placeholder="(00) 00000-0000" />
        </ReferenceCard>
      </div>
    );
  }

  /** E-mail ou telefone — espelha o mobile (toggleRow + referenceField condicional) */
  if (step.type === "text_input" && message === "email_ou_telefone") {
    return (
      <div>
        <StepHeader emoji={emoji} title={label} />
        {description && <StepDescription>{description}</StepDescription>}
        <ReferenceCard>
          <div className="mb-3 flex gap-2">
            <ToggleWide active>E-mail</ToggleWide>
            <ToggleWide>Telefone</ToggleWide>
          </div>
          <ReferenceLabel>E-mail vinculado</ReferenceLabel>
          <FakeInput placeholder="exemplo@email.com" />
        </ReferenceCard>
      </div>
    );
  }

  /** Chave PIX (preset) — espelha o mobile (label "Tipo da chave" + 4 toggles + field condicional) */
  if (step.type === "text_input" && message === "chave_pix") {
    return (
      <div>
        <StepHeader emoji={emoji} title={label} />
        {description && <StepDescription>{description}</StepDescription>}
        <ReferenceCard>
          <ReferenceLabel>Tipo da chave (vinculada no seu nome)</ReferenceLabel>
          <div className="mb-3 grid grid-cols-2 gap-2">
            <ToggleWide>Celular</ToggleWide>
            <ToggleWide active>E-mail</ToggleWide>
            <ToggleWide>CPF</ToggleWide>
            <ToggleWide>Aleatória</ToggleWide>
          </div>
          <ReferenceLabel>E-mail</ReferenceLabel>
          <FakeInput placeholder="exemplo@email.com" />
        </ReferenceCard>
      </div>
    );
  }

  switch (step.type) {
    case "cpf_lookup":
      return (
        <div>
          <StepHeader emoji={emoji} title={label} />
          <StepDescription>{t("flow_editor.preview.helper.cpf")}</StepDescription>
          <div className="mt-3">
            <FieldLabel>CPF</FieldLabel>
            <FakeInput placeholder="000.000.000-00" />
          </div>
        </div>
      );

    case "otp_whatsapp":
      return (
        <div>
          <StepHeader emoji={emoji} title={label} />
          <StepDescription>{t("flow_editor.preview.helper.whatsapp")}</StepDescription>
          <div className="mt-3">
            <FieldLabel>WhatsApp</FieldLabel>
            <FakeInput placeholder="(00) 00000-0000" />
          </div>
        </div>
      );

    case "cep_lookup":
      return (
        <div>
          <StepHeader emoji={emoji} title={label} />
          {description && <StepDescription>{description}</StepDescription>}
          <div className="mt-3">
            <FieldLabel>CEP</FieldLabel>
            {/* mobile usa input + botao verde "Buscar" */}
            <div className="flex gap-2">
              <div
                className="flex-1 rounded-xl px-3 py-2.5 text-[11px]"
                style={{
                  backgroundColor: C.inputBg,
                  border: `1px solid ${C.inputBorder}`,
                  color: "#999",
                }}
              >
                00000-000
              </div>
              <div
                className="rounded-xl px-3 text-[11px] font-bold leading-[34px] text-white"
                style={{ backgroundColor: C.primary }}
              >
                Buscar
              </div>
            </div>
            <div className="mt-3">
              <FieldLabel>Rua</FieldLabel>
              <FakeInput placeholder={t("flow_editor.preview.ph.street")} />
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <div>
                <FieldLabel>Número</FieldLabel>
                <FakeInput placeholder="123" />
              </div>
              <div>
                <FieldLabel>Compl.</FieldLabel>
                <FakeInput placeholder="—" />
              </div>
            </div>
          </div>
        </div>
      );

    case "text_input":
      return (
        <div>
          <StepHeader emoji={emoji} title={label} />
          {description && <StepDescription>{description}</StepDescription>}
          <FakeInput placeholder={placeholder} />
        </div>
      );

    case "multiple_choice":
      return (
        <div>
          <StepHeader emoji={emoji} title={label} />
          {description && <StepDescription>{description}</StepDescription>}
          <div className="mt-3 flex flex-col gap-2">
            {(step.config.options || []).map((opt, i) => {
              const text = typeof opt === "string" ? opt : opt.label ?? opt.value ?? "";
              return (
                <OptionCard key={i} variant="radio">
                  {text}
                </OptionCard>
              );
            })}
            {(step.config.options || []).length === 0 && (
              <div className="text-[10px]" style={{ color: C.textHint }}>
                {t("flow_editor.preview.no_options")}
              </div>
            )}
          </div>
        </div>
      );

    case "multi_select":
      return (
        <div>
          <StepHeader emoji={emoji} title={label} />
          {description && <StepDescription>{description}</StepDescription>}
          <div className="mt-3 flex flex-col gap-2">
            {(step.config.options || []).map((opt, i) => {
              const text = typeof opt === "string" ? opt : opt.label ?? opt.value ?? "";
              return (
                <OptionCard key={i} variant="checkbox">
                  {text}
                </OptionCard>
              );
            })}
            {(step.config.options || []).length === 0 && (
              <div className="text-[10px]" style={{ color: C.textHint }}>
                {t("flow_editor.preview.no_options")}
              </div>
            )}
          </div>
        </div>
      );

    case "document_upload": {
      // Quando o gestor configura `fields`, renderiza 1 slot labelado por
      // arquivo (ex: "Frente" + "Verso"). Caso contrario, single slot.
      const docFields = (step.config.fields as DocumentFieldConfig[] | undefined) || [];
      const maxSizeHint = step.config.maxSizeMB
        ? t("flow_editor.preview.max_size", { mb: step.config.maxSizeMB })
        : undefined;
      if (docFields.length > 1) {
        return (
          <div>
            <StepHeader emoji={emoji} title={label} />
            {description && <StepDescription>{description}</StepDescription>}
            <div className="flex flex-col gap-2.5">
              {docFields.map((f, i) => (
                <div key={i}>
                  <ReferenceLabel>{f.label}</ReferenceLabel>
                  <DashedActionBox
                    icon="📄"
                    title={t("flow_editor.preview.upload_tap")}
                    hint={maxSizeHint}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      }
      // Single slot (fallback ou 1 field). Label do step ja explica.
      return (
        <div>
          <StepHeader emoji={emoji} title={label} />
          {description && <StepDescription>{description}</StepDescription>}
          <DashedActionBox
            icon="📄"
            title={t("flow_editor.preview.upload_tap")}
            hint={maxSizeHint}
          />
        </div>
      );
    }

    case "selfie":
      return (
        <div>
          <StepHeader emoji={emoji} title={label} />
          {description && <StepDescription>{description}</StepDescription>}
          <DashedActionBox
            icon="📸"
            title="Abrir câmera"
            hint={step.config.camera === "back" ? "Câmera traseira" : "Câmera frontal"}
            tall
          />
        </div>
      );

    case "video_recording":
      return (
        <div>
          <StepHeader emoji={emoji} title={label} />
          {description && <StepDescription>{description}</StepDescription>}
          {message && (
            <div
              className="mt-2 whitespace-pre-wrap break-words rounded-[14px] px-3 py-2.5 text-[11px] font-medium leading-snug"
              style={{
                backgroundColor: "#FFF8E1",
                border: "1px solid #FFE082",
                color: "#5D4037",
              }}
            >
              {message}
            </div>
          )}
          <DashedActionBox
            icon="🎥"
            title="Gravar vídeo"
            hint={`Máx. ${step.config.maxDurationSeconds || 60} segundos`}
            tall
          />
        </div>
      );

    case "date_input":
      return (
        <div>
          <StepHeader emoji={emoji} title={label} />
          {description && <StepDescription>{description}</StepDescription>}
          <FakeInput placeholder={placeholder || "DD/MM/AAAA"} />
        </div>
      );

    case "currency_input":
      return (
        <div>
          <StepHeader emoji={emoji} title={label} />
          {description && <StepDescription>{description}</StepDescription>}
          <div
            className="mt-2 flex items-center rounded-xl"
            style={{
              backgroundColor: C.inputBg,
              border: `1px solid ${C.inputBorder}`,
            }}
          >
            <span
              className="pl-3 text-[12px] font-semibold"
              style={{ color: C.textTitle }}
            >
              R$
            </span>
            <span className="flex-1 px-2 py-2.5 text-[11px]" style={{ color: "#999" }}>
              0,00
            </span>
          </div>
        </div>
      );

    case "number_input":
      return (
        <div>
          <StepHeader emoji={emoji} title={label} />
          {description && <StepDescription>{description}</StepDescription>}
          <FakeInput placeholder={placeholder || "0"} />
        </div>
      );

    case "info_screen": {
      const requireAccept = Boolean((step.config as { requireAccept?: boolean }).requireAccept);
      const acceptText =
        (step.config as { acceptText?: string }).acceptText ||
        "Li, entendi e aceito os termos acima";
      const bullets = (step.config as { bullets?: string[] }).bullets;
      const alertText = (step.config as { alert?: string }).alert;
      return (
        <div>
          <StepHeader emoji={emoji} title={label} />
          <div
            className="mt-3 rounded-2xl p-3.5"
            style={{
              backgroundColor: C.infoBg,
              border: `1px solid ${C.infoBorder}`,
            }}
          >
            {message && (
              <p
                className="whitespace-pre-wrap break-words text-[11px] font-normal leading-relaxed"
                style={{ color: C.textBody }}
              >
                {message}
              </p>
            )}
            {Array.isArray(bullets) && bullets.length > 0 && (
              <div className="mt-3 flex flex-col gap-1.5">
                {bullets.map((b, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="text-[12px] font-bold" style={{ color: C.primary }}>
                      ✓
                    </span>
                    <span className="flex-1 text-[11px] leading-snug" style={{ color: "#334155" }}>
                      {b}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {alertText && (
              <div
                className="mt-3 rounded-md px-2.5 py-2 text-[10px] font-semibold"
                style={{
                  backgroundColor: "#FEF3C7",
                  borderLeft: "4px solid #F59E0B",
                  color: "#92400E",
                }}
              >
                ⚠ {alertText}
              </div>
            )}
          </div>
          {requireAccept && <AcceptCard text={acceptText} />}
        </div>
      );
    }

    case "loan_simulator": {
      // Pega o 1o plano configurado no wizard. Se houver multiplos (diario +
      // semanal etc), gestor ve a simulacao do primeiro — o mobile mostra
      // seletor pro cliente. Sem planos cai no placeholder R$ 0,00.
      const plan = simulatorPlans?.[0];
      const valorBase = Math.max(parseFloat(simulatorValorMinimo || "0") || 0, 0);
      const taxa = plan ? parseFloat(plan.taxaPct) || 0 : 0;
      /*
       * Juros simples × numero de parcelas — bate com a formula do
       * mobile (LoanSimulatorStep). taxaPct e por periodo (dia/semana/
       * quinzena/mes); total = base * (1 + taxa * parcelas).
       */
      const valorTotal = valorBase * (1 + (taxa / 100) * (plan?.parcelas ?? 0));
      const valorParcela = plan && plan.parcelas > 0 ? valorTotal / plan.parcelas : 0;
      const tipoLbl = plan ? TIPO_LABELS_PT[plan.tipo] || { plural: plan.tipo } : null;
      return (
        <div>
          <StepHeader emoji={emoji} title={label} />
          {description && <StepDescription>{description}</StepDescription>}

          {/* Card gradiente verde (header com valor solicitado) — igual mobile */}
          <div
            className="mt-3 rounded-2xl p-3.5 text-white"
            style={{
              background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
            }}
          >
            <div className="text-[10px] uppercase tracking-wide opacity-80">
              {t("flow_editor.preview.simulator.amount")}
            </div>
            <div className="text-lg font-bold">
              {plan ? formatBRL(valorBase) : "R$ 0,00"}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-[10px]">
              <div>
                <div className="opacity-80">
                  {t("flow_editor.preview.simulator.installments")}
                </div>
                <div className="font-semibold">
                  {plan ? `${plan.parcelas}x ${tipoLbl?.plural || ""}` : "12x"}
                </div>
              </div>
              <div>
                <div className="opacity-80">
                  {t("flow_editor.preview.simulator.rate")}
                </div>
                <div className="font-semibold">
                  {plan ? `${taxa.toLocaleString("pt-BR")}%` : "--%"}
                </div>
              </div>
            </div>
          </div>

          {/* Detalhamento da simulacao quando ha plano */}
          {plan && (
            <div
              className="mt-2 rounded-2xl p-3"
              style={{
                backgroundColor: "#fff",
                border: `1px solid ${C.cardBorderStrong}`,
              }}
            >
              <div
                className="text-[9px] font-bold uppercase tracking-wide"
                style={{ color: C.textHint }}
              >
                Simulação
              </div>
              <div className="mt-1.5 flex items-center justify-between text-[11px]">
                <span style={{ color: C.textHint }}>Parcelas</span>
                <span className="font-semibold" style={{ color: C.textTitle }}>
                  {plan.parcelas}x {tipoLbl?.plural}
                </span>
              </div>
              <div className="mt-1 flex items-center justify-between text-[11px]">
                <span style={{ color: C.textHint }}>Valor da parcela</span>
                <span
                  className="font-bold"
                  style={{ color: C.primary }}
                >
                  {formatBRL(valorParcela)}
                </span>
              </div>
              <div className="mt-1 flex items-center justify-between text-[11px]">
                <span style={{ color: C.textHint }}>Total a pagar</span>
                <span className="font-semibold" style={{ color: C.textTitle }}>
                  {formatBRL(valorTotal)}
                </span>
              </div>
              {(simulatorPlans?.length ?? 0) > 1 && (
                <div
                  className="mt-2 text-[9px] italic"
                  style={{ color: C.textHint }}
                >
                  Existem {simulatorPlans!.length} planos — mostrando o
                  primeiro. No app o cliente escolhe.
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    case "create_password": {
      const minLen = step.config.minLength ?? 6;
      return (
        <div>
          <StepHeader emoji={emoji} title={label} />
          {description && <StepDescription>{description}</StepDescription>}
          <div className="mt-3">
            <FieldLabel>Senha</FieldLabel>
            <div
              className="flex items-center rounded-xl"
              style={{
                backgroundColor: C.inputBg,
                border: `1px solid ${C.inputBorder}`,
              }}
            >
              <span className="flex-1 px-3 py-2.5 text-[11px]" style={{ color: "#999" }}>
                {`Mínimo ${minLen} caracteres`}
              </span>
              <span
                className="px-3 text-[11px] font-semibold"
                style={{ color: C.primary }}
              >
                Ver
              </span>
            </div>
            <div className="mt-3">
              <FieldLabel>Confirmar senha</FieldLabel>
              <div
                className="flex items-center rounded-xl"
                style={{
                  backgroundColor: C.inputBg,
                  border: `1px solid ${C.inputBorder}`,
                }}
              >
                <span className="flex-1 px-3 py-2.5 text-[11px]" style={{ color: "#999" }}>
                  {t("flow_editor.preview.ph.confirm_password")}
                </span>
                <span
                  className="px-3 text-[11px] font-semibold"
                  style={{ color: C.primary }}
                >
                  Ver
                </span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    case "references": {
      const quantity = (step.config as { quantity?: number }).quantity ?? 2;
      return (
        <div>
          <StepHeader emoji={emoji} title={label} />
          <StepDescription>
            {t("flow_editor.preview.helper.references", { n: quantity })}
          </StepDescription>
          <div className="mt-2 flex flex-col gap-2.5">
            {Array.from({ length: quantity }).map((_, i) => (
              <div
                key={i}
                className="rounded-2xl p-3"
                style={{
                  backgroundColor: "#fff",
                  border: `1.5px solid ${C.cardBorderStrong}`,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                }}
              >
                <div
                  className="text-[9px] font-bold uppercase tracking-wide"
                  style={{ color: C.textHint }}
                >
                  {t("flow_editor.preview.reference_n", { n: i + 1 })}
                </div>
                <FakeInput placeholder={t("flow_editor.preview.ph.name")} />
                <FakeInput placeholder="(00) 00000-0000" />
              </div>
            ))}
          </div>
        </div>
      );
    }

    case "terms":
    case "penalty_warning":
      return (
        <div>
          <StepHeader emoji={emoji} title={label} />
          {message && (
            <div
              className="mt-3 rounded-2xl p-3.5"
              style={{
                backgroundColor: C.infoBg,
                border: `1px solid ${C.infoBorder}`,
              }}
            >
              <p
                className="whitespace-pre-wrap break-words text-[11px] font-normal leading-relaxed"
                style={{ color: C.textBody }}
              >
                {message}
              </p>
            </div>
          )}
          <AcceptCard text={t("flow_editor.preview.accept_terms")} />
        </div>
      );

    case "pix_input":
      return (
        <div>
          <StepHeader emoji={emoji} title={label} />
          {description && <StepDescription>{description}</StepDescription>}
          <ReferenceCard>
            <ReferenceLabel>Tipo da chave (vinculada no seu nome)</ReferenceLabel>
            <div className="mb-3 grid grid-cols-2 gap-2">
              <ToggleWide>Celular</ToggleWide>
              <ToggleWide active>E-mail</ToggleWide>
              <ToggleWide>CPF</ToggleWide>
              <ToggleWide>Aleatória</ToggleWide>
            </div>
            <ReferenceLabel>E-mail</ReferenceLabel>
            <FakeInput placeholder={t("flow_editor.preview.ph.pix_key")} />
          </ReferenceCard>
        </div>
      );

    case "conditional_input": {
      const opts: ConditionalInputOption[] = step.config.inputOptions || [];
      if (opts.length === 0) {
        return (
          <div>
            <StepHeader emoji={emoji} title={label} />
            {description && <StepDescription>{description}</StepDescription>}
            <div className="text-[10px]" style={{ color: C.textHint }}>
              {t("flow_editor.preview.no_options")}
            </div>
          </div>
        );
      }
      // Preview mostra a 1a opcao como selecionada por padrao
      const activeIdx = 0;
      const active = opts[activeIdx];
      // 2 colunas se forem ate 4 opcoes; 1 coluna se forem mais (linhas)
      const cols = opts.length <= 4 ? 2 : 1;
      return (
        <div>
          <StepHeader emoji={emoji} title={label} />
          {description && <StepDescription>{description}</StepDescription>}
          <ReferenceCard>
            <div
              className="mb-3 grid gap-2"
              style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
            >
              {opts.map((opt, i) => (
                <ToggleWide key={i} active={i === activeIdx}>
                  {opt.label || `Opção ${i + 1}`}
                </ToggleWide>
              ))}
            </div>
            {active.input.label && <ReferenceLabel>{active.input.label}</ReferenceLabel>}
            <FakeInput placeholder={active.input.placeholder} />
          </ReferenceCard>
        </div>
      );
    }

    default:
      return (
        <div>
          <StepHeader emoji={emoji} title={label || step.type} />
          <StepDescription>
            {t("flow_editor.preview.unsupported", { type: step.type })}
          </StepDescription>
        </div>
      );
  }
}

export default function FlowPreview({
  steps,
  selectedStepId,
  simulatorPlans,
  simulatorValorMinimo,
}: FlowPreviewProps) {
  const { t } = useTranslation();

  const sortedSteps = useMemo(
    () => [...steps].sort((a, b) => a.order - b.order),
    [steps],
  );

  const [internalIndex, setInternalIndex] = useState(0);

  useEffect(() => {
    if (!selectedStepId) return;
    const idx = sortedSteps.findIndex((s) => s.id === selectedStepId);
    if (idx >= 0) setInternalIndex(idx);
  }, [selectedStepId, sortedSteps]);

  // Clamp if steps shrink
  useEffect(() => {
    if (internalIndex >= sortedSteps.length && sortedSteps.length > 0) {
      setInternalIndex(sortedSteps.length - 1);
    } else if (sortedSteps.length === 0 && internalIndex !== 0) {
      setInternalIndex(0);
    }
  }, [sortedSteps.length, internalIndex]);

  const currentStep = sortedSteps[internalIndex];
  const isLast = internalIndex === sortedSteps.length - 1;

  return (
    <div className="w-[300px] shrink-0 border-l border-[var(--border)] bg-[var(--background)]">
      <PhoneFrame
        stepCount={sortedSteps.length}
        currentIndex={internalIndex}
        onPrev={() => setInternalIndex((i) => Math.max(0, i - 1))}
        onNext={() => setInternalIndex((i) => Math.min(sortedSteps.length - 1, i + 1))}
        emptyMessage={t("flow_editor.preview.empty")}
        isLast={isLast}
      >
        {currentStep && (
          <StepPreview
            step={currentStep}
            simulatorPlans={simulatorPlans}
            simulatorValorMinimo={simulatorValorMinimo}
          />
        )}
      </PhoneFrame>
    </div>
  );
}
